import {
  CancelableFunc,
  CancelablePromise,
  chainAbortSignal,
  Func,
  MaybeCancelableFunc,
} from "./cancelable-promise";
import {
  PaginatedResponseDto as _PaginatedResponseDto,
  PaginatedResponseWithCountDto as _PaginatedResponseWithCountDto,
  ApiError as _ApiError,
} from "./__generated__";
import {
  queryOptions,
  UseQueryOptions,
  UseMutationOptions,
  QueryKey,
} from "@tanstack/react-query";
import { isDefined } from "@/lib/utils";
import { MergeDeep, PartialDeep } from "type-fest";
import _ from "lodash";

export type ShiftTuple<T extends any[]> = T extends [T[0], ...infer R]
  ? R
  : never; // https://blog.e-mundo.de/post/typescript-tuple-trickery-utility-types-for-tuples/
export type PopTuple<T extends any[]> = T extends [...infer R, infer _E]
  ? R
  : never; // https://blog.e-mundo.de/post/typescript-tuple-trickery-utility-types-for-tuples/
export type LastInTuple<T extends any[]> = T extends [...infer _R, infer E]
  ? E
  : never; // https://blog.e-mundo.de/post/typescript-tuple-trickery-utility-types-for-tuples/

// adds all properties of requestBody to the first argument of the request, and making the added properties optional
export const extendRequestBody = <B extends Record<any, any>>(
  requestBody: B,
) => {
  type BKeys = B extends Record<infer K, any> ? K : never;
  return <T extends (...args: any[]) => any>(target: T) => {
    type P = Parameters<T>;
    type R = ReturnType<T>;
    type P0 = P[0];
    type P0Mod = Omit<P0, "requestBody"> & {
      requestBody: Omit<P0["requestBody"], BKeys> &
        Partial<Pick<P0["requestBody"], BKeys>>;
    };
    type PRest = ShiftTuple<P> extends never ? [] : ShiftTuple<P>;
    return (p0mod: P0Mod, ...rest: PRest): R => {
      const p0: P0 = {
        ...p0mod,
        requestBody: { ...requestBody, ...(p0mod?.requestBody ?? {}) },
      };
      return target(p0, ...rest);
    };
  };
};

export interface PaginatedResponseDto<T>
  extends Omit<_PaginatedResponseDto, "results"> {
  results: T[];
}

export interface PaginatedResponseWithCountDto<T>
  extends Omit<_PaginatedResponseWithCountDto, "results"> {
  results: T[];
}

type SingleOrMany<T> = T extends [] ? void : T extends [infer K] ? K : T;

export type ApiError = Omit<_ApiError, "body"> & {
  body: { statusCode?: number; message?: string };
};

export const isApiError = (ex?: any): ex is ApiError => {
  return (
    isDefined(ex?.body) ||
    isDefined(ex?.url) ||
    isDefined(ex?.status) ||
    isDefined(ex?.statusText) ||
    isDefined(ex?.body) ||
    isDefined(ex?.request)
  );
};

export const extend = <T extends Func, TQueryKey extends QueryKey = [string]>(
  action: T,
  options?: { queryKey?: TQueryKey; name?: string },
) => {
  if (!action) {
    throw new Error(
      "extend() called with undefined action. Check that the service method exists in the generated API client.",
    );
  }
  const name = action.name || options?.name;
  let _queryKey = options?.queryKey;
  if (!_queryKey) {
    if (!name) {
      console.warn({ action }, "extended without a name / queryKey");
    }
    _queryKey = [name] as unknown as TQueryKey;
  }
  return Object.assign(action, {
    queryKey: _queryKey,
    asQueryOptions: <TError = ApiError>(...args: Parameters<T>) =>
      ({
        queryFn: ({ queryKey, signal }) => {
          return chainAbortSignal(
            action(...queryKey.slice(_queryKey.length)),
            signal,
          ) as Awaited<ReturnType<T>>;
        },
        queryKey: [..._queryKey, ...args],
        staleTime: 30 * 1000, // default stale time
      }) as UseQueryOptions<
        Awaited<ReturnType<T>>,
        TError,
        Awaited<ReturnType<T>>,
        [...TQueryKey, ...Parameters<T>]
      >,
    asMutationOptions: <TError = ApiError, TContext = unknown>() => {
      return {
        mutationFn: (args) =>
          action.length > 1 && Array.isArray(args)
            ? action(...(args as Parameters<T>))
            : action(args as Parameters<T>[0]),
        mutationKey: _queryKey,
      } as UseMutationOptions<
        Awaited<ReturnType<T>>,
        TError,
        SingleOrMany<Parameters<T>>,
        TContext
      >;
    },
    assign: <K extends PartialDeep<Parameters<T>[0]>>(
      data: K,
      options?: { name?: string; queryKey?: any[]; omitData?: boolean },
    ) => {
      return extend(
        (
          arg: MergeDeep<Exclude<Parameters<T>[0], undefined>, PartialDeep<K>>,
        ) => action(_.merge({}, data, arg)) as ReturnType<T>,
        // extendFirstArg(data)(action),
        {
          queryKey: [
            ..._queryKey,
            ...(options?.queryKey
              ? options.queryKey
              : options?.name
                ? [options.name]
                : []),
            ...(!options?.omitData ? [data] : []),
          ],
        },
      );
    },
  });
};

export type ExtendedAction<
  T extends Func,
  TQueryKey extends QueryKey = [string],
> = ReturnType<typeof extend<T, TQueryKey>>;

export const autoCancel = <T extends CancelableFunc<any>>(req: T) => {
  type Args = Parameters<T>;
  type Ret = T extends (...args: any[]) => CancelablePromise<infer Ret>
    ? Ret
    : never;
  let promise: CancelablePromise<Ret> | undefined;
  const cancel = () => {
    if (promise) {
      promise.cancel();
      promise = undefined;
    }
  };
  const _do = (...args: Args) => {
    cancel();
    const retPromise = req(...args) as CancelablePromise<Ret>;
    promise = retPromise;
    return retPromise;
  };
  _do.cancel = cancel;
  return _do;
};

export const extendFirstArg = <B extends Record<any, any>>(items: B) => {
  type BKeys = B extends Record<infer K, any> ? K : never;
  return <T extends (...args: any[]) => any>(target: T) => {
    type P = Parameters<T>;
    type R = ReturnType<T>;
    type P0Mod = Omit<P[0], BKeys>;
    type PRest = ShiftTuple<P> extends never ? [] : ShiftTuple<P>;
    return (p0mod?: P0Mod, ...rest: PRest): R =>
      target({ ...p0mod, ...items }, ...rest);
  };
};

// export const assign = <T extends Object = any, K = any>(target: T, additional: K) =>
//   Object.assign(target, additional) as T & K;

export const toRecord = (params: any): any =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([_key, val]) => val !== undefined && val !== "",
    ),
  );

const qsStart = (query: string) => (query ? `?${query}` : "");
export const qs = (params?: Record<string, any>) =>
  params ? qsStart(new URLSearchParams(toRecord(params)).toString()) : "";

export const asQuery = <T extends MaybeCancelableFunc>(
  apiCall: T,
  data: Parameters<T>[0],
) => {
  return queryOptions({
    queryFn: async ({ queryKey, signal }) => {
      const [_, data] = queryKey;
      return await chainAbortSignal(apiCall({ signal, ...data }), signal);
    },
    queryKey: [apiCall.name, data],
  });
};
